<script setup lang="ts">
import TheIcon from '@/Components/TheIcon.vue'
import { Listing, ReviewsData } from '../interfaces/listing'
import { ref } from 'vue'
import ListingSwiper from './ListingSwiper.vue'
import ReviewItem from './ReviewItem.vue'

type Props = {
    listing: Listing;
    reviews?: ReviewsData | '',
    bookHostelsHref: string | undefined,
    hostelIsClosed: boolean
}

const props = withDefaults(defineProps<Props>(), {
    reviews: '',
    bookHostelsHref: undefined
})


const showSwiperGallery = ref(false)
const initialSlide = ref(0)

const toggleShowSwiperGallery = (index: number): void => {
    initialSlide.value = index
    showSwiperGallery.value = !showSwiperGallery.value
}

const breakpointsThumb ={
    '576': {
        slidesPerView: 2.7
    },
    '1200': {
        slidesPerView: 3.5
    },
    '1400': {
        slidesPerView: 4.5
    }
}

</script>

<template>
    <div class="listing-modal-content p-4">
        <div class="d-flex align-items-center justify-content-center mb-3 listing-modal-content-header" :class="{'listing-modal-content-header--with-back-btn': showSwiperGallery}">
            <Transition name="fade-in-out">
                <div
                    v-if="showSwiperGallery"
                    class="close-slider-btn d-flex align-items-center justify-content-center cursor-pointer"
                    @click="toggleShowSwiperGallery(0)"
                >
                    <TheIcon
                        icon-id="arrow-prev"
                        width="24"
                        height="24"
                        stroke="#4A5268"
                    />
                </div>
            </Transition>
            
            <slot v-if="$slots.header" name="header" />
        </div>
        <div class="d-flex listing-modal-content-wrap">
            <transition name="slide-right">
                <div v-if="!showSwiperGallery" class="listing-modal-content-images-wrap">
                    <div v-if="listing.images.length" class="listing-modal-content-images d-flex flex-wrap">
                        <div
                            v-for="(img, index) in listing.images"
                            :key="index"
                            class="listing-modal-content-image cursor-pointer"
                            :index-image="index"
                        >
                            <picture v-if="img.src">
                                <source :srcset="img.src.thumb_webp" type="image/webp">
                                <source :srcset="img.src.thumb_def" type="image/jpg">

                                <img
                                    :alt="img.title"
                                    :title="img.title"
                                    loading="lazy"
                                    @click="toggleShowSwiperGallery(index)"
                                >
                            </picture>
                        </div>
                    </div>
                </div>
                
                <div v-else class="listing-modal-content-wrap-slider" :class="{'listing-modal-content-wrap-slider--no-reviews': !reviews || reviews.reviews.length === 0}">
                    <ListingSwiper
                        v-if="listing.images.length"
                        :initial-slide="initialSlide"
                        :listing-images="listing.images"
                        :navigation-main="true"
                        :space-between-thumb="16"
                        :breakpoints-thumb="breakpointsThumb"
                        class="listing-modal-content-slider"
                    />
                </div>
            </transition>
            
            <div v-if="reviews && reviews.reviews.length !== 0" class="listing-modal-content-reviews">
                <div
                    v-if="listing.formatCombinedRating"
                    class="listing-modal-content-rating-wrap d-flex align-items-center mb-3"
                >
                    <div class="listing-modal-content-rating d-flex align-items-center justify-content-center mr-2">
                        <span
                            class="combinedRating"
                            :content="listing.formatCombinedRating"
                        >
                            {{ listing.formatCombinedRating }}
                        </span>
                    </div>

                    <div v-if="listing.combinedRatingCount" class="nowrap cl-subtext listing-top-rating-reviews">
                        <span>{{ listing.combinedRatingCount }} reviews</span>
                    </div>
                </div>
                <ReviewItem :reviews="reviews.reviews" :column-view="true" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.listing-modal-content {
    position: relative;
    border-radius: 16px;
    
    &-header {
        padding-right:60px;

        @media (min-width: 1200px) {
            padding-right: 0;     
        }

        &--with-back-btn {
            padding-left: 60px;

            @media (min-width: 1200px) {
                padding-left: 0;     
            }
        }
    }

    &-avatar {
        color: var(--light-bg);

        background-color: var(--main-text);
        border-radius: 50%;
        width: 33px;
        height: 33px;

        @media (min-width: 992px) {
            width: 44px;
            height: 44px;
        }

        &:deep(.the-icon-component) {
            svg {
                filter: brightness(200%);
            }
        }  
    }
    

    &-wrap {
        gap: 30px;
        overflow: hidden;
        height: calc(95vh - 104px);

        @media (min-width: 576px) {
            height: calc(95vh - 126px);
        }

        @media (min-width: 768px) {
            height: calc(95vh - 112px);
        }


        @media (min-width: 992px) {
            height: calc(95vh - 112px);
        }

        &-slider {
            max-width: calc(100% - 270px);
            height: 85vh;
            overflow-y: auto;

            &--no-reviews {
                max-width: 100%;
            }

            &:deep(.listing-modal-content-slider) {
               display: flex;
               flex-direction: column;
               height: 100%;
            }

            &:deep(.listing-swiper) {
                max-width: 100%;
                flex: 2;
                margin-bottom: 8px;

                .swiper-button-next, .swiper-button-prev {
                    color: var(--primary-brand);
                
                    &::after {
                        height: 100%;
                        width: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                }
            }

            &:deep(.listing-swiper-thumb) {
                max-width: 100%;
                max-height: 173px;
                flex: 1;
                width: 100%;
            }
        }
    }

    &-images {
        gap: 8px;
        
        &-wrap {
            overflow-y: auto;
        }
    }

     &-image {
        min-height: 200px;
        flex-basis: calc(50% - 4px); 
        overflow: hidden;

        @media (min-width: 1200px) {
            flex-basis: calc(33% - 3px);      
        }

        &:hover {
            img {
                transform: scale(1.1);
            }
        }

        img {
            transition: transform .2s ease;
            object-fit: cover;
            max-width: 100%;
            height: 100%;
            width: 100%;
        }
    }

    &-reviews {
        max-width: 240px;
        flex-shrink: 0;
        width: 100%;
        overflow-y: auto;

        @media (min-width: 1200px) {
            max-width: 260px;  
        }

        @media (min-width: 1400px) {
            max-width: 280px;  
        }

        &-stars {
            gap: 8px;
        }

        &-text {
            font-size: 16px;
            font-weight: 400;
            line-height: 1.4;
            margin: 0;
            padding: 0;
        
            &-wrap {

                &:deep(*) {
                    font-size: 16px;
                    font-weight: 400;
                    line-height: 1.4;
                    margin: 0;
                    padding: 0;
                }
            }
        }


        &-wrap {
            &:not(:last-child) {
                padding-bottom: 16px;
                border-bottom: 1px solid var(--light-bg);
                margin-bottom: 16px;
            
                @media (min-width: 992px) {
                    padding-bottom: 32px;
                    margin-bottom: 32px;
                }
            }
            
        }
    }

    &-rating {
        font-family: var(--font-family-poppins-sans-serif);
        font-size: 16px;
        font-weight: 600;
        line-height: 1.4;
        color: var(--light-bg);

        background-color: var(--main-text);
        border-radius: 50%;
        width: 33px;
        height: 33px;

        @media (min-width: 992px) {
            width: 44px;
            height: 44px;
            font-size: 24px;
        }
    }
}

.slide-right-enter-active, .slide-right-leave-active {
   transition: transform .5s ease-out, opacity .5s ease-out;
}

.slide-right-leave-active {
    position: absolute;
    max-width: calc(100% - (240px + 30px + 24px + 24px));
}

.slide-right-enter-from, .slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.slide-right-enter-to, .slide-right-leave-from {
  transform: translateX(0);
  opacity: 1;
}

.close-slider-btn {
    border-radius: 50%;
    position: absolute;
    width: 44px;
    height: 44px;
    background-color: var(--light-bg);

    box-shadow: 0px 6px 2px -4px #0E132C14;

    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, opacity 4.15s ease-in-out;

    top: 20px;
    left: 24px;

    @media (min-width: 992px) {
       top: 26px;
    }

    &:hover {
        background-color: #e2e6ea;
    }

    &:deep(svg) {
        display: flex;
    }
}

.fade-in-out-enter-active, .fade-in-out-leave-active {
  transition: opacity 0.5s;
}
.fade-in-out-enter-from, .fade-in-out-leave-to {
  opacity: 0;
}
.fade-in-out-enter-to, .fade-in-out-leave-from {
  opacity: 1;
}

.hostelIsClosed {
    background-color: #ff733d;
    transition:  background-color 0.3s;
    cursor: pointer;
    color: white;

    &:hover {
        background-color: #cc5c30;
        border-color: #cc5c30;
        color: white;

        svg {
            fill: #cc5c30;
        }
    }

    svg {
        transition: fill 0.3s;
        fill: #ff733d;
    }
}
</style>

